import { useState } from "react";
import { Loader2, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const NONE = "__unassigned__";
const ADD = "__add__";

/**
 * Who is looking after this enquiry.
 *
 * The list is the firm's own team roster, so a name can never be a typo in one
 * row and correct in another. Adding a person is offered from inside the same
 * menu, because the moment you notice someone is missing is the moment you are
 * trying to hand them the work.
 */
export const OwnerPicker = ({
  value,
  members = [],
  saving = false,
  onAssign,
  onAddMember,
  testId = "admin-owner",
  label = "Assigned to",
}) => {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [addingSaving, setAddingSaving] = useState(false);

  const choose = (next) => {
    if (next === ADD) {
      setAdding(true);
      return;
    }
    if (next === NONE) {
      if (value) onAssign(null);
      return;
    }
    if (next !== value) onAssign(next);
  };

  const submitName = async (event) => {
    event.preventDefault();
    const typed = name.trim();
    if (!typed || addingSaving) return;
    setAddingSaving(true);
    const added = await onAddMember(typed);
    setAddingSaving(false);
    if (!added) return;
    setName("");
    setAdding(false);
    onAssign(added);
  };

  /* Someone can leave the roster while still holding work. Their name stays
     selectable here so the row never looks unassigned by accident. */
  const known = members.some((member) => member.name === value);
  const stale = value && !known ? value : null;

  return (
    <>
      <div className="flex items-center gap-2">
        <Select value={value || NONE} onValueChange={choose} disabled={saving}>
          <SelectTrigger
            aria-label={label}
            className="h-9 w-[168px] text-xs"
            data-testid={`${testId}-select`}
          >
            <SelectValue placeholder="Unassigned" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE} data-testid={`${testId}-option-unassigned`}>
              Unassigned
            </SelectItem>
            {members.map((member) => (
              <SelectItem
                key={member.id || member.name}
                value={member.name}
                data-testid={`${testId}-option`}
              >
                {member.name}
              </SelectItem>
            ))}
            {stale ? (
              <SelectItem value={stale} data-testid={`${testId}-option-former`}>
                {stale} (no longer on the team)
              </SelectItem>
            ) : null}
            <SelectSeparator />
            <SelectItem value={ADD} data-testid={`${testId}-option-add`}>
              Add a team member…
            </SelectItem>
          </SelectContent>
        </Select>
        {saving ? (
          <Loader2
            className="h-3.5 w-3.5 animate-spin text-muted-foreground"
            aria-hidden="true"
          />
        ) : null}
      </div>

      <Dialog open={adding} onOpenChange={(open) => (open ? null : setAdding(false))}>
        <DialogContent className="max-w-md" data-testid="admin-owner-add-dialog">
          <form onSubmit={submitName}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="h-4 w-4" aria-hidden="true" />
                Add a team member
              </DialogTitle>
              <DialogDescription>
                Names you add here can be assigned to any enquiry that is waiting on us.
              </DialogDescription>
            </DialogHeader>

            <div className="py-4">
              <Label htmlFor="admin-owner-name" className="text-xs font-semibold">
                Their name
              </Label>
              <Input
                id="admin-owner-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="e.g. Thandi"
                maxLength={60}
                autoComplete="off"
                className="mt-1.5"
                data-testid="admin-owner-name-input"
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setAdding(false)}
                data-testid="admin-owner-add-cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={name.trim().length < 2 || addingSaving}
                data-testid="admin-owner-add-save"
              >
                {addingSaving ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                ) : null}
                Add and assign
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};
